import { useCallback, useEffect, useRef, useState } from 'react';

/** File change data for the file tree */
export interface FileChangeItem {
  filePath: string;
  changeType: 'ADDED' | 'MODIFIED' | 'DELETED' | 'RENAMED';
  additions: number;
  deletions: number;
}

export interface FileTreeProps {
  /** List of changed files to display */
  files: FileChangeItem[];
  /** Currently selected file path */
  selectedFile?: string;
  /** Callback when a file is clicked */
  onFileSelect?: (filePath: string) => void;
}

/** Maps change type to display config */
export const CHANGE_TYPE_CONFIG: Record<
  string,
  { label: string; className: string; description: string }
> = {
  ADDED: {
    label: 'A',
    className: 'bg-success/15 text-success',
    description: 'Added',
  },
  MODIFIED: {
    label: 'M',
    className: 'bg-warning/15 text-warning',
    description: 'Modified',
  },
  DELETED: {
    label: 'D',
    className: 'bg-danger/15 text-danger',
    description: 'Deleted',
  },
  RENAMED: {
    label: 'R',
    className: 'bg-accent-100 text-accent-700 dark:bg-accent-900 dark:text-accent-300',
    description: 'Renamed',
  },
};

function ChangeTypeIndicator({ changeType }: { changeType: string }) {
  const config = CHANGE_TYPE_CONFIG[changeType] ?? {
    label: '?',
    className: 'bg-surface-raised text-content-secondary',
    description: 'Unknown',
  };

  return (
    <span
      className={`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-xs font-medium ${config.className}`}
      aria-hidden="true"
      title={config.description}
    >
      {config.label}
    </span>
  );
}

function FileTreeList({ files, selectedFile, onFileSelect }: FileTreeProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (files.length === 0) return;

      let newIndex = focusedIndex;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          newIndex = focusedIndex < files.length - 1 ? focusedIndex + 1 : 0;
          break;
        case 'ArrowUp':
          e.preventDefault();
          newIndex = focusedIndex > 0 ? focusedIndex - 1 : files.length - 1;
          break;
        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          newIndex = files.length - 1;
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < files.length) {
            const file = files[focusedIndex];
            if (file) {
              onFileSelect?.(file.filePath);
            }
          }
          return;
        default:
          return;
      }

      setFocusedIndex(newIndex);
      // Focus the button at the new index
      const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]');
      buttons?.[newIndex]?.focus();
    },
    [files, focusedIndex, onFileSelect],
  );

  return (
    <ul
      ref={listRef}
      role="listbox"
      aria-label="Changed files"
      className="space-y-0.5"
      onKeyDown={handleKeyDown}
    >
      {files.map((file, index) => {
        const isSelected = file.filePath === selectedFile;
        return (
          <li key={file.filePath} role="presentation">
            <button
              role="option"
              aria-selected={isSelected}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 ${
                isSelected
                  ? 'bg-brand-50 text-brand-800 dark:bg-brand-950 dark:text-brand-200'
                  : 'text-content-secondary hover:bg-surface-raised'
              }`}
              onClick={() => onFileSelect?.(file.filePath)}
              onFocus={() => setFocusedIndex(index)}
              tabIndex={index === 0 && focusedIndex === -1 ? 0 : focusedIndex === index ? 0 : -1}
              aria-label={`${file.filePath} (${CHANGE_TYPE_CONFIG[file.changeType]?.description ?? 'unknown'}, +${file.additions} -${file.deletions})`}
            >
              <ChangeTypeIndicator changeType={file.changeType} />
              <span className="min-w-0 flex-1 truncate font-mono text-xs">
                {file.filePath}
              </span>
              <span className="flex-shrink-0 text-xs">
                <span className="text-success">+{file.additions}</span>
                {' '}
                <span className="text-danger">-{file.deletions}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * FileTree component that displays changed files with type indicators and line counts.
 * On desktop (≥1024px) it renders as a sidebar panel.
 * Below 1024px it collapses to a disclosure/drawer that can be toggled open/closed.
 */
export function FileTree({ files, selectedFile, onFileSelect }: FileTreeProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close drawer when clicking outside on mobile
  useEffect(() => {
    if (!drawerOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        setDrawerOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [drawerOpen]);

  // Close drawer on Escape
  useEffect(() => {
    if (!drawerOpen) return;

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [drawerOpen]);

  // Close drawer when a file is selected on mobile
  const handleFileSelect = useCallback(
    (filePath: string) => {
      onFileSelect?.(filePath);
      setDrawerOpen(false);
    },
    [onFileSelect],
  );

  if (files.length === 0) {
    return (
      <div className="text-sm text-content-tertiary">
        No files changed.
      </div>
    );
  }

  return (
    <>
      {/* Desktop: always-visible sidebar panel (≥1024px) */}
      <nav
        aria-label="Changed files"
        className="hidden lg:block"
        data-testid="file-tree-desktop"
      >
        <div className="rounded-lg border border-border bg-surface p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-tertiary">
            Files Changed ({files.length})
          </h3>
          <FileTreeList
            files={files}
            selectedFile={selectedFile}
            onFileSelect={onFileSelect}
          />
        </div>
      </nav>

      {/* Mobile: collapsible drawer (<1024px) */}
      <div className="lg:hidden" data-testid="file-tree-mobile" ref={drawerRef}>
        <button
          onClick={() => setDrawerOpen(!drawerOpen)}
          aria-expanded={drawerOpen}
          aria-controls="file-tree-drawer"
          className="flex w-full items-center justify-between rounded-lg border border-border bg-surface px-3 py-2 text-sm font-medium text-content hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600"
        >
          <span>Files Changed ({files.length})</span>
          <svg
            className={`h-4 w-4 transition-transform ${drawerOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {drawerOpen && (
          <nav
            id="file-tree-drawer"
            aria-label="Changed files"
            className="mt-1 rounded-lg border border-border bg-surface p-3"
          >
            <FileTreeList
              files={files}
              selectedFile={selectedFile}
              onFileSelect={handleFileSelect}
            />
          </nav>
        )}
      </div>
    </>
  );
}
